// MYOB Service with Firebase Database Storage
import { 
    doc, 
    setDoc, 
    getDoc, 
    deleteDoc,
    collection,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import { db } from 'config/firebase.config';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

const MYOB_CONFIG = {
    clientId: 'ffe6161b-1635-4bf8-b113-371caad049c1',
    clientSecret: 'TgJ6J3dg10KEYS5dEaxSF1Ls',
    redirectUri: 'https://timesheet.billbergia.com.au/callback',
    authUrl: 'https://secure.myob.com/oauth2/account/authorize',
    tokenUrl: 'https://secure.myob.com/oauth2/v1/authorize',
    scope: 'sme-company-file sme-payroll sme-contacts-employee',
    
    // API Configuration with Checkpoint Security Wrapper
    apiBaseUrl: 'https://protect.checkpoint.com/v2/r04/___https://api.myob.com/accountright/',
    checkpointSuffix: '___Y3A0YTpiaWxsYmVyZ2lhOmM6bzo2YWYxMjBhODY2MGQ0Nzk2NDEzYjVjNTMzY2QxNDNkMTo3OjMzMzc6NTMwYzcxNzg3OWU5M2U2YWFiOTI5OTNiOTUwYmNiZGU3OGQ3YmU1NGJkMTRlNzUzYjZmYmZlYjM0ZWE2ZjhkMzp0OlQ6Tg',
    
    // Leave Balance Endpoints
    leaveBalanceEndpoints: {
        findEmployee: '/Contact/Employee?$filter=DisplayID eq \'{displayId}\'',
        getPayrollDetails: '/Contact/EmployeePayrollDetails/{employeeUid}'
    }
};

// Fallback business ID for Billbergia (used when business ID can't be retrieved from API)
const FALLBACK_BUSINESS_ID = '4d26aa09-701b-4992-b263-e56db0b73bc6';
const FALLBACK_BUSINESS_NAME = 'Billbergia Project Management Pty Ltd';

class MYOBFirebaseService {
    constructor() {
        this.currentUserId = null;
        this.tokens = null;
        this.functions = getFunctions();
    }

    // Set current user ID
    setCurrentUser(userId) {
        this.currentUserId = userId;
    }

    // Build API URL - use direct API URL for better compatibility
    buildApiUrl(companyFileId, endpoint = '') {
        // Use direct API URL instead of Checkpoint wrapper for better compatibility
        return `https://api.myob.com/accountright/${companyFileId}${endpoint}`;
    }

    // Generate a unique state parameter for CSRF protection
    generateState() {
        const timestamp = Date.now().toString(36);
        const random1 = Math.random().toString(36).substring(2, 15);
        const random2 = Math.random().toString(36).substring(2, 15);
        return `myob_${timestamp}_${random1}_${random2}`;
    }

    // Get the authorization URL
    getAuthorizationUrl(state) {
        const params = new URLSearchParams({
            client_id: MYOB_CONFIG.clientId,
            redirect_uri: MYOB_CONFIG.redirectUri,
            response_type: 'code',
            scope: MYOB_CONFIG.scope,
            state: state,
            prompt: 'consent'
        });

        return `${MYOB_CONFIG.authUrl}?${params.toString()}`;
    }

    // Save tokens to Firebase Firestore
    async saveTokensToFirebase(tokens) {
        if (!this.currentUserId) {
            throw new Error('No user ID set. Call setCurrentUser() first.');
        }

        try {
            const tokenData = {
                ...tokens,
                userId: this.currentUserId,
                updatedAt: new Date(),
                createdAt: tokens.createdAt || new Date()
            };

            await setDoc(doc(db, 'myob_tokens', this.currentUserId), tokenData);
            this.tokens = tokens;
            
            console.log('✅ Tokens saved to Firebase Firestore');
            return true;
        } catch (error) {
            console.error('❌ Error saving tokens to Firebase:', error);
            throw error;
        }
    }

    // Load tokens from Firebase Firestore
    async loadTokensFromFirebase() {
        console.log('🔍 Loading tokens from Firebase...');
        console.log('Current user ID:', this.currentUserId);
        
        if (!this.currentUserId) {
            console.warn('❌ No user ID set. Will try to load shared tokens.');
            return await this.loadSharedTokens();
        }

        try {
            // First try to load user-specific tokens
            const docRef = doc(db, 'myob_tokens', this.currentUserId);
            console.log('📡 Fetching document from:', docRef.path);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                const tokenData = docSnap.data();
                this.tokens = tokenData;
                console.log('✅ Tokens loaded from Firebase Firestore');
                console.log('Token data:', {
                    hasAccessToken: !!this.tokens.access_token,
                    hasRefreshToken: !!this.tokens.refresh_token,
                    expiresAt: this.tokens.expires_at ? new Date(this.tokens.expires_at).toISOString() : 'No expiry',
                    businessId: this.tokens.business_id,
                    businessName: this.tokens.business_name
                });
                return tokenData;
            } else {
                console.log('❌ No tokens found in Firebase for user:', this.currentUserId);
                console.log('🔄 Trying to load shared tokens...');
                return await this.loadSharedTokens();
            }
        } catch (error) {
            console.error('❌ Error loading tokens from Firebase:', error);
            throw error;
        }
    }

    // Load shared MYOB tokens (used by all users to fetch real-time data)
    async loadSharedTokens() {
        console.log('🔍 Loading shared MYOB tokens...');
        
        try {
            // Try to find any tokens in the myob_tokens collection
            const tokensCollection = collection(db, 'myob_tokens');
            const tokensSnapshot = await getDocs(tokensCollection);
            
            if (!tokensSnapshot.empty) {
                // Get the first available token document
                const firstTokenDoc = tokensSnapshot.docs[0];
                const tokenData = firstTokenDoc.data();
                
                if (tokenData && tokenData.refresh_token) {
                    this.tokens = tokenData;
                    console.log('✅ Loaded shared MYOB tokens from:', firstTokenDoc.id);
                    console.log('Token data:', {
                        hasAccessToken: !!this.tokens.access_token,
                        hasRefreshToken: !!this.tokens.refresh_token,
                        expiresAt: this.tokens.expires_at ? new Date(this.tokens.expires_at).toISOString() : 'No expiry',
                        businessId: this.tokens.business_id,
                        businessName: this.tokens.business_name
                    });
                    return tokenData;
                }
            }
            
            console.log('❌ No shared tokens found in Firebase');
            return null;
        } catch (error) {
            console.error('❌ Error loading shared tokens:', error);
            throw error;
        }
    }

    // Clear tokens from Firebase
    async clearTokensFromFirebase() {
        if (!this.currentUserId) {
            throw new Error('No user ID set. Cannot clear tokens from Firebase.');
        }

        try {
            await deleteDoc(doc(db, 'myob_tokens', this.currentUserId));
            this.tokens = null;
            console.log('✅ Tokens cleared from Firebase Firestore');
            return true;
        } catch (error) {
            console.error('❌ Error clearing tokens from Firebase:', error);
            throw error;
        }
    }

    // Update refresh token in Firebase (for debugging/manual updates)
    async updateRefreshToken(newRefreshToken) {
        if (!this.currentUserId) {
            throw new Error('No user ID set. Cannot update refresh token.');
        }

        try {
            console.log('🔄 Updating refresh token in Firebase...');
            
            // Load current tokens first
            await this.loadTokensFromFirebase();
            
            if (!this.tokens) {
                throw new Error('No existing tokens found to update');
            }

            // Update the refresh token
            const updatedTokens = {
                ...this.tokens,
                refresh_token: newRefreshToken,
                updatedAt: new Date()
            };

            // Save to Firebase
            await this.saveTokensToFirebase(updatedTokens);
            
            // Update local tokens
            this.tokens = updatedTokens;
            
            console.log('✅ Refresh token updated successfully');
            return updatedTokens;
        } catch (error) {
            console.error('❌ Error updating refresh token:', error);
            throw error;
        }
    }

    // Exchange authorization code for tokens
    async exchangeCodeForTokens(code, state, businessId = null, businessName = null) {
        try {
            console.log('🔧 MYOB Firebase Service: Starting token exchange...');
            console.log('Code:', code.substring(0, 30) + '...');
            console.log('State:', state);
            console.log('Business ID from callback:', businessId);
            console.log('Business Name from callback:', businessName);
            console.log('Token URL:', MYOB_CONFIG.tokenUrl);
            console.log('Client ID:', MYOB_CONFIG.clientId);
            console.log('Redirect URI:', MYOB_CONFIG.redirectUri);
            
            const requestBody = new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: MYOB_CONFIG.clientId,
                client_secret: MYOB_CONFIG.clientSecret,
                redirect_uri: MYOB_CONFIG.redirectUri,
                code: code,
                state: state
            });

            console.log('Request body:', requestBody.toString());
            
            // Use Firebase Function to avoid CORS issues
            const functionUrl = 'https://us-central1-billbergia-timesheets.cloudfunctions.net/exchangeMYOBToken';
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code, state })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Function call failed: ${response.status}`);
            }

            const result = await response.json();
            const tokenData = result.tokens;
            console.log('✅ Token exchange successful:', {
                hasAccessToken: !!tokenData.access_token,
                hasRefreshToken: !!tokenData.refresh_token,
                expiresIn: tokenData.expires_in,
                scope: tokenData.scope,
                businessId: tokenData.business_id,
                businessName: tokenData.business_name
            });
            
            // Debug: Log all fields in the token response
            console.log('🔍 Full token response fields:', Object.keys(tokenData));
            console.log('🔍 Token response values:', tokenData);
            
            // Store tokens with expiration
            const tokens = {
                access_token: tokenData.access_token,
                refresh_token: tokenData.refresh_token,
                expires_at: Date.now() + (tokenData.expires_in * 1000),
                scope: tokenData.scope,
                business_id: businessId || tokenData.business_id || null,
                business_name: businessName || tokenData.business_name || null,
                createdAt: new Date()
            };

            console.log('🔄 Saving tokens to Firebase...');
            console.log('💾 Business ID being saved:', tokens.business_id);
            console.log('💾 Business Name being saved:', tokens.business_name);
            // Save to Firebase instead of localStorage
            await this.saveTokensToFirebase(tokens);
            console.log('💾 Tokens saved to Firebase database successfully');
            
            return tokens;
        } catch (error) {
            console.error('❌ Error exchanging code for tokens:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            throw error;
        }
    }

    // Get valid access token (refresh if needed)
    async getValidAccessToken() {
        console.log('🔍 Getting valid access token...');
        
        if (!this.tokens) {
            console.log('📥 Loading tokens from Firebase...');
            await this.loadTokensFromFirebase();
        }

        if (!this.tokens?.access_token) {
            console.log('❌ No access token available');
            throw new Error('No access token available');
        }

        console.log('✅ Access token found');
        console.log('Token expires at:', new Date(this.tokens.expires_at).toISOString());
        console.log('Current time:', new Date().toISOString());
        console.log('Is expired:', Date.now() >= this.tokens.expires_at);

        // Check if token is expired
        if (Date.now() >= this.tokens.expires_at) {
            console.log('🔄 Token is expired, refreshing...');
            if (this.tokens.refresh_token) {
                await this.refreshAccessToken();
            } else {
                console.log('❌ No refresh token available');
                throw new Error('Access token expired and no refresh token available');
            }
        } else {
            console.log('✅ Token is still valid');
        }

        return this.tokens.access_token;
    }

    // Refresh access token using refresh token
    async refreshAccessToken() {
        console.log('🔄 MYOB Firebase Service: Starting token refresh...');
        console.log('Current tokens in service:', this.tokens);
        
        if (!this.tokens?.refresh_token) {
            console.log('❌ No refresh token available in service');
            throw new Error('No refresh token available');
        }

        console.log('✅ Refresh token found in service, making API call...');
        console.log('Token URL:', MYOB_CONFIG.tokenUrl);
        console.log('Client ID:', MYOB_CONFIG.clientId);

        try {
            const requestBody = new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: MYOB_CONFIG.clientId,
                client_secret: MYOB_CONFIG.clientSecret,
                refresh_token: this.tokens.refresh_token
            });

            console.log('Request body:', requestBody.toString());

            let response;
            let tokenData;

            try {
                // Try Firebase Function first
                console.log('🔄 Trying Firebase Function for token refresh...');
                const functionUrl = 'https://us-central1-billbergia-timesheets.cloudfunctions.net/refreshMYOBToken';
                response = await fetch(functionUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ refreshToken: this.tokens.refresh_token })
                });

                console.log('Firebase Function response status:', response.status, response.statusText);

                if (!response.ok) {
                    throw new Error(`Firebase Function failed: ${response.status}`);
                }

                const result = await response.json();
                tokenData = result.tokens;
                console.log('✅ Firebase Function token refresh success');
            } catch (firebaseError) {
                console.log('⚠️ Firebase Function failed, trying direct API call...', firebaseError.message);
                
                // Fallback to direct API call
                response = await fetch(MYOB_CONFIG.tokenUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: requestBody
                });

                console.log('Direct API response status:', response.status, response.statusText);

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('❌ Direct API token refresh error:', errorData);
                    throw new Error(errorData.error_description || errorData.error || `Token refresh failed: ${response.status}`);
                }

                tokenData = await response.json();
                console.log('✅ Direct API token refresh success');
            }

            console.log('✅ Token refresh success:', {
                hasAccessToken: !!tokenData.access_token,
                hasRefreshToken: !!tokenData.refresh_token,
                expiresIn: tokenData.expires_in
            });
            
            // Update tokens with new ones
            this.tokens = {
                ...this.tokens,
                access_token: tokenData.access_token,
                refresh_token: tokenData.refresh_token, // ✅ NEW REFRESH TOKEN
                expires_at: Date.now() + (tokenData.expires_in * 1000)
            };

            console.log('🔄 Saving updated tokens to Firebase...');
            // Save updated tokens to Firebase
            await this.saveTokensToFirebase(this.tokens);
            console.log('✅ Tokens saved to Firebase successfully');
            
            return this.tokens;
        } catch (error) {
            console.error('❌ Error refreshing access token:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            throw error;
        }
    }

    // Make authenticated request
    async makeAuthenticatedRequest(url) {
        try {
            console.log('🔍 Making authenticated request to:', url);
            const accessToken = await this.getValidAccessToken();
            console.log('✅ Access token obtained for request');
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'x-myobapi-key': MYOB_CONFIG.clientId,
                    'x-myobapi-version': 'v2',
                    'Accept': 'application/json'
                }
            });

            console.log('📡 Response status:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ API Error Response:', errorText);
                
                // If it's a 401, the token might be invalid
                if (response.status === 401) {
                    console.log('🔄 Got 401, token might be invalid, trying to refresh...');
                    // Try to refresh token and retry once
                    try {
                        await this.refreshAccessToken();
                        const newAccessToken = await this.getValidAccessToken();
                        
                        console.log('🔄 Retrying request with new token...');
                        const retryResponse = await fetch(url, {
                            method: 'GET',
                            headers: {
                                'Authorization': `Bearer ${newAccessToken}`,
                                'x-myobapi-key': MYOB_CONFIG.clientId,
                                'x-myobapi-version': 'v2',
                                'Accept': 'application/json'
                            }
                        });
                        
                        if (!retryResponse.ok) {
                            const retryErrorText = await retryResponse.text();
                            console.error('❌ Retry also failed:', retryErrorText);
                            throw new Error(`API request failed after retry: ${retryResponse.status} ${retryResponse.statusText}. ${retryErrorText}`);
                        }
                        
                        console.log('✅ Retry successful');
                        return retryResponse;
                    } catch (retryError) {
                        console.error('❌ Retry failed:', retryError);
                        throw new Error(`API request failed: ${response.status} ${response.statusText}. Token refresh also failed: ${retryError.message}`);
                    }
                }
                
                throw new Error(`API request failed: ${response.status} ${response.statusText}. ${errorText}`);
            }

            console.log('✅ Request successful');
            return response;
        } catch (error) {
            console.error('❌ Error in makeAuthenticatedRequest:', error);
            throw error;
        }
    }

    // Get company files
    async getCompanyFiles() {
        const response = await this.makeAuthenticatedRequest('https://api.myob.com/accountright/');
        return response.json();
    }

    // Get current tokens info
    getTokensInfo() {
        if (!this.tokens) return null;
        
        return {
            business_id: this.tokens.business_id,
            business_name: this.tokens.business_name,
            scope: this.tokens.scope,
            expires_at: new Date(this.tokens.expires_at).toLocaleString(),
            user_id: this.tokens.user_id,
            createdAt: this.tokens.createdAt?.toLocaleString(),
            updatedAt: this.tokens.updatedAt?.toLocaleString()
        };
    }

    // Check if user is authenticated
    isAuthenticated() {
        return !!this.tokens?.access_token;
    }

    // Manually set business ID (useful for testing or when API fails)
    async setBusinessId(businessId, businessName = null) {
        if (!this.tokens) {
            throw new Error('No tokens available. Please authenticate first.');
        }

        const updatedTokens = {
            ...this.tokens,
            business_id: businessId,
            business_name: businessName || FALLBACK_BUSINESS_NAME,
        };

        await this.saveTokensToFirebase(updatedTokens);
        this.tokens = updatedTokens;

        console.log('✅ Business ID manually set:', businessId);
        return businessId;
    }

    // Test connection to MYOB API
    async testConnection() {
        try {
            console.log('🔍 Testing MYOB API connection...');
            
            // Load tokens first
            if (!this.tokens) {
                await this.loadTokensFromFirebase();
            }
            
            if (!this.tokens?.refresh_token) {
                throw new Error('No refresh token available');
            }
            
            console.log('🔄 Refreshing access token for test...');
            
            // Use Firebase Function to avoid CORS issues
            const refreshFunctionUrl = 'https://us-central1-billbergia-timesheets.cloudfunctions.net/refreshMYOBToken';
            const response = await fetch(refreshFunctionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken: this.tokens.refresh_token })
            });

            console.log('Token refresh response status:', response.status, response.statusText);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('❌ Token refresh failed:', errorData);
                throw new Error(`Token refresh failed: ${errorData.error || errorData.message || response.statusText}`);
            }

            const refreshResult = await response.json();
            const tokenData = refreshResult.tokens;
            console.log('✅ Token refresh successful');
            
            const businessId = await this.ensureBusinessId();
            console.log('✅ Business ID obtained:', businessId);
            
            // Test with the working endpoint using Firebase Function
            const functionUrl = 'https://us-central1-billbergia-timesheets.cloudfunctions.net/callMYOBAPI';
            const endpoint = '/Contact/EmployeePayrollDetails?$top=1&$skip=0';
            
            console.log('📡 Testing API call via Firebase Function...');
            
            const apiResponse = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accessToken: tokenData.access_token,
                    businessId: businessId,
                    endpoint: endpoint
                })
            });

            console.log(`📡 Test response status: ${apiResponse.status} ${apiResponse.statusText}`);

            if (!apiResponse.ok) {
                const errorData = await apiResponse.json();
                console.error('❌ Test API Error Response:', errorData);
                throw new Error(`API test failed: ${apiResponse.status} ${apiResponse.statusText}. ${errorData.error || 'Unknown error'}`);
            }

            const result = await apiResponse.json();
            const data = result.data;
            console.log('✅ API test successful, retrieved', data.Items?.length || 0, 'employee records');
            
            return {
                success: true,
                businessId: businessId,
                message: `Connection test successful - retrieved ${data.Items?.length || 0} employee records`
            };
        } catch (error) {
            console.error('❌ Connection test failed:', error);
            return {
                success: false,
                error: error.message,
                message: 'Connection test failed'
            };
        }
    }

    // Ensure business ID is available (get from company files if not in tokens)
    async ensureBusinessId() {
        console.log('🔍 Checking for business ID...');
        console.log('Current tokens:', this.tokens);
        
        if (this.tokens?.business_id) {
            console.log('✅ Business ID found in tokens:', this.tokens.business_id);
            return this.tokens.business_id;
        }

        console.log('⚠️ No business ID in tokens, fetching company files...');
        try {
            const companyFiles = await this.getCompanyFiles();
            console.log('Company files response:', companyFiles);
            
            if (companyFiles && companyFiles.Items && companyFiles.Items.length > 0) {
                const businessId = companyFiles.Items[0].Id;
                const businessName = companyFiles.Items[0].Name;
                
                console.log('✅ Found company file:', { businessId, businessName });
                
                // Update tokens with business info
                const updatedTokens = {
                    ...this.tokens,
                    business_id: businessId,
                    business_name: businessName,
                };
                
                await this.saveTokensToFirebase(updatedTokens);
                this.tokens = updatedTokens;
                
                console.log('✅ Business ID retrieved and saved:', businessId);
                return businessId;
            } else {
                console.log('❌ No company files found in response');
                throw new Error('No company files found. Please ensure you have access to a MYOB company file.');
            }
        } catch (error) {
            console.error('❌ Error getting business ID:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            
            // Fallback to hardcoded business ID for Billbergia
            console.log('🔄 Using fallback business ID for Billbergia...');
            console.log('✅ Using fallback business ID:', FALLBACK_BUSINESS_ID);
            
            // Update tokens with fallback business info
            const updatedTokens = {
                ...this.tokens,
                business_id: FALLBACK_BUSINESS_ID,
                business_name: FALLBACK_BUSINESS_NAME,
            };
            
            await this.saveTokensToFirebase(updatedTokens);
            this.tokens = updatedTokens;
            
            console.log('✅ Fallback business ID saved:', FALLBACK_BUSINESS_ID);
            return FALLBACK_BUSINESS_ID;
        }
    }

    // Get all employees from a company file
    async getAllEmployees(companyFileId) {
        try {
            // Validate company file ID
            if (!companyFileId) {
                throw new Error('Company file ID is required');
            }

            console.log('👥 Getting employees for company file:', companyFileId);
            
            const accessToken = await this.getValidAccessToken();
            console.log('✅ Access token obtained for employee fetch');
            
            const employees = [];
            let skip = 0;
            const top = 1000;
            let hasMore = true;

            while (hasMore) {
                const url = `https://api.myob.com/accountright/${companyFileId}/Contact/Employee?$top=${top}&$skip=${skip}`;
                console.log(`📡 Fetching employees from: ${url}`);
                
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'x-myobapi-key': MYOB_CONFIG.clientId,
                        'x-myobapi-version': 'v2'
                    }
                });

                console.log(`📡 Response status: ${response.status} ${response.statusText}`);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ API Error Response:', errorText);
                    throw new Error(`Failed to get employees: ${response.status} ${response.statusText}. ${errorText}`);
                }

                const data = await response.json();
                const items = data.Items || [];
                
                console.log(`✅ Retrieved ${items.length} employees (skip: ${skip})`);
                employees.push(...items);
                
                hasMore = items.length === top;
                skip += top;
            }

            console.log(`🎉 Total employees retrieved: ${employees.length}`);
            return employees;
        } catch (error) {
            console.error('❌ Error getting employees:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name,
                companyFileId: companyFileId
            });
            throw error;
        }
    }

    // Leave Balance Methods (based on MYOB Support Email)
    
    // Find employee by DisplayID (e.g., '100')
    async findEmployeeByDisplayId(companyFileId, displayId) {
        try {
            const endpoint = MYOB_CONFIG.leaveBalanceEndpoints.findEmployee.replace('{displayId}', displayId);
            const url = this.buildApiUrl(companyFileId, endpoint);
            
            console.log('🔍 Finding employee by DisplayID:', displayId);
            console.log('📡 API URL:', url);
            
            const response = await this.makeAuthenticatedRequest(url);
            return response.json();
        } catch (error) {
            console.error('Error finding employee:', error);
            throw error;
        }
    }

    // Get employee payroll details including leave balances
    async getEmployeeLeaveBalances(companyFileId, employeeUid) {
        try {
            const endpoint = MYOB_CONFIG.leaveBalanceEndpoints.getPayrollDetails.replace('{employeeUid}', employeeUid);
            const url = this.buildApiUrl(companyFileId, endpoint);
            
            console.log('🔍 Getting leave balances for employee UID:', employeeUid);
            console.log('📡 API URL:', url);
            
            const response = await this.makeAuthenticatedRequest(url);
            return response.json();
        } catch (error) {
            console.error('Error getting leave balances:', error);
            throw error;
        }
    }

    // Complete workflow: Get leave balances by employee DisplayID
    async getLeaveBalancesByDisplayId(companyFileId, displayId) {
        try {
            console.log('🎯 Getting leave balances for DisplayID:', displayId);
            
            // Ensure we have a valid business ID
            const businessId = companyFileId || await this.ensureBusinessId();
            console.log('✅ Using business ID for leave balance lookup:', businessId);
            
            // Step 1: Find employee by DisplayID
            const employeeResponse = await this.findEmployeeByDisplayId(businessId, displayId);
            
            if (!employeeResponse || !employeeResponse.Items || employeeResponse.Items.length === 0) {
                throw new Error(`Employee with DisplayID '${displayId}' not found`);
            }
            
            const employee = employeeResponse.Items[0];
            const employeeUid = employee.UID;
            
            console.log('✅ Found employee:', employee.DisplayID, employee.Name, 'UID:', employeeUid);
            
            // Step 2: Get payroll details (leave balances)
            const leaveBalances = await this.getEmployeeLeaveBalances(businessId, employeeUid);
            
            return {
                employee: employee,
                leaveBalances: leaveBalances
            };
        } catch (error) {
            console.error('Error in leave balance workflow:', error);
            throw error;
        }
    }

    // Get all employees with their leave data using the working Contact/EmployeePayrollDetails endpoint
    async getAllEmployeesWithLeaveData(companyFileId) {
        try {
            console.log('👥 Getting all employees with leave data...');
            
            // Load tokens first
            if (!this.tokens) {
                await this.loadTokensFromFirebase();
            }
            
            if (!this.tokens?.refresh_token) {
                throw new Error('No refresh token available');
            }
            
            console.log('🔄 Refreshing access token for employee data...');
            
            // Use Firebase Function to avoid CORS issues
            const functionUrl = 'https://us-central1-billbergia-timesheets.cloudfunctions.net/refreshMYOBToken';
            const tokenResponse = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken: this.tokens.refresh_token })
            });

            console.log('Token refresh response status:', tokenResponse.status, tokenResponse.statusText);

            if (!tokenResponse.ok) {
                const errorData = await tokenResponse.json();
                console.error('❌ Token refresh failed:', errorData);
                throw new Error(`Token refresh failed: ${errorData.error || errorData.message || tokenResponse.statusText}`);
            }

            const result = await tokenResponse.json();
            const tokenData = result.tokens;
            console.log('✅ Token refresh successful');
            
            // Ensure we have a valid business ID
            const businessId = companyFileId || await this.ensureBusinessId();
            console.log('✅ Using business ID for employee leave data:', businessId);
            
            // Use Firebase Function to avoid CORS issues
            const apiFunctionUrl = 'https://us-central1-billbergia-timesheets.cloudfunctions.net/callMYOBAPI';
            const endpoint = '/Contact/EmployeePayrollDetails';
            
            console.log('📡 Calling Firebase Function for MYOB API...');
            
            const response = await fetch(apiFunctionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accessToken: tokenData.access_token,
                    businessId: businessId,
                    endpoint: endpoint
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`API request failed: ${response.status} ${response.statusText}. ${errorData.error || 'Unknown error'}`);
            }
            
            const apiResult = await response.json();
            const data = apiResult.data;
            console.log(`✅ Retrieved ${data.Items.length} employee payroll records`);
            
            // Process the data to extract employee info and leave balances
            const employeesWithLeave = data.Items.map(item => {
                const employee = item.Employee;
                const entitlements = item.Entitlements || [];
                
                // Debug: Log the structure of the first item to understand the data format
                if (data.Items.indexOf(item) === 0) {
                    console.log('🔍 Debugging first employee data structure:');
                    console.log('Employee:', employee);
                    console.log('Entitlements:', entitlements);
                    if (entitlements.length > 0) {
                        console.log('First entitlement structure:', entitlements[0]);
                        console.log('EntitlementCategory:', entitlements[0]?.EntitlementCategory);
                    }
                }
                
                // Find annual leave entitlement
                const annualLeave = entitlements.find(ent => 
                    ent.EntitlementCategory.Name.includes('Holiday Leave') || 
                    ent.EntitlementCategory.Name.includes('Annual Leave')
                );
                
                // Debug: Log annual leave structure for Paul Allsop
                if (employee.Name.includes('Paul Allsop')) {
                    console.log('🔍 Paul Allsop entitlements:', entitlements.map(e => e.EntitlementCategory.Name));
                    console.log('🔍 Paul Allsop annual leave found:', annualLeave);
                }
                
                // Debug: Log annual leave structure
                if (annualLeave && data.Items.indexOf(item) === 0) {
                    console.log('🏖️ Annual Leave structure:', annualLeave);
                    console.log('Available fields:', Object.keys(annualLeave));
                }
                
                // Find long service leave entitlement
                const longServiceLeave = entitlements.find(ent => 
                    ent.EntitlementCategory.Name.includes('Long Service Leave')
                );
                
                return {
                    uid: employee.UID,
                    name: employee.Name,
                    displayId: employee.DisplayID,
                    firstName: employee.Name.split(' ')[0] || '',
                    lastName: employee.Name.split(' ').slice(1).join(' ') || '',
                    isActive: !item.TerminationDate,
                    startDate: item.StartDate,
                    terminationDate: item.TerminationDate,
                    annualSalary: item.Wage?.AnnualSalary || 0,
                    hourlyRate: item.Wage?.HourlyRate || 0,
                    payFrequency: item.Wage?.PayFrequency || '',
                    leaveBalances: {
                        annualLeave: annualLeave ? {
                            total: annualLeave.Total || 0,
                            carryOver: annualLeave.CarryOver || 0,
                            yearToDate: annualLeave.YearToDate || 0,
                            isAssigned: annualLeave.IsAssigned || false,
                            totalHours: annualLeave.Total || 0
                        } : null,
                        longServiceLeave: longServiceLeave ? {
                            total: longServiceLeave.Total || 0,
                            carryOver: longServiceLeave.CarryOver || 0,
                            yearToDate: longServiceLeave.YearToDate || 0,
                            isAssigned: longServiceLeave.IsAssigned || false,
                            totalHours: longServiceLeave.Total || 0
                        } : null
                    }
                };
            });
            
            console.log(`✅ Processed ${employeesWithLeave.length} employees with leave data`);
            return employeesWithLeave;
            
        } catch (error) {
            console.error('Error getting employees with leave data:', error);
            throw error;
        }
    }

    // Get all users with MYOB tokens (admin function)
    async getAllUsersWithTokens() {
        try {
            const q = query(collection(db, 'myob_tokens'));
            const querySnapshot = await getDocs(q);
            
            const users = [];
            querySnapshot.forEach((doc) => {
                users.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            return users;
        } catch (error) {
            console.error('Error getting users with tokens:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const myobFirebaseService = new MYOBFirebaseService();
export default myobFirebaseService;
